import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDefaultCurrency } from '../utils/countryCurrencyUtils';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // Initialize with currency from localStorage or detect from location
  const getInitialCurrency = () => {
    try {
      const saved = localStorage.getItem('selectedCurrency');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading saved currency:', error);
    }
    // Return null to trigger detection
    return null;
  };

  const [selectedCurrency, setSelectedCurrency] = useState(getInitialCurrency);
  const [conversionRate, setConversionRate] = useState(1.0); // Default conversion rate
  const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);

  // Detect user's currency on first load if not saved
  useEffect(() => {
    const detectAndSetCurrency = async () => {
      if (selectedCurrency) return; // Already have a currency
      
      setIsDetectingCurrency(true);
      try {
        const detectedCurrency = await getDefaultCurrency();
        setSelectedCurrency(detectedCurrency);
        // Save the detected currency to localStorage
        localStorage.setItem('selectedCurrency', JSON.stringify(detectedCurrency));
      } catch (error) {
        console.error('Error detecting currency:', error);
        // Fallback to USD
        const fallbackCurrency = { code: 'USD', name: 'US Dollar' };
        setSelectedCurrency(fallbackCurrency);
        localStorage.setItem('selectedCurrency', JSON.stringify(fallbackCurrency));
      } finally {
        setIsDetectingCurrency(false);
      }
    };

    detectAndSetCurrency();
  }, [selectedCurrency]);

  const value = {
    selectedCurrency,
    setSelectedCurrency,
    conversionRate,
    setConversionRate,
    isDetectingCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
